import { parseAlgodError, parseContractError } from "@/lib/utils";
import { CopyButton } from "./copy-button";
import { HelpButton } from "./help-button";
import { Link } from "./links";
import { CogIcon, RefreshCcw } from "lucide-react";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "./ui/button";

function errorCodetoMessage(code: string) {
  let msg;
  switch (code) {
    case "ERR UNAUTH":
    case "ERR:UNAUTH":
      msg = (
        <>
          <strong>You are not authorized to perform this action.</strong>
          <br />
          <br />
          <strong>
            This should not happen. Please contact our team for resolution.
          </strong>
        </>
      );
      break;
    case "ERR:NOT STARTED":
      msg = (
        <>
          This bench has not opened yet. If you see this error, your computer
          clock may not be properly synchronized.
          <br />
          <br />
          <strong>Please try again after the bench has opened.</strong>
        </>
      );
      break;
    case "ERR:ENDED":
      msg = <>This bench has closed. This usually means that it sold out.</>;
      break;
    case "ERR:BLK RNGE":
      msg = (
        <>
          Insufficient transaction validity.
          <br />
          <br />
          <strong>
            This should never happen. Please let our team know about this error.
            Diagnostics will help investigate the issue.
          </strong>
        </>
      );
      break;
    case "ERR:OVERSELL":
      msg = (
        <>
          Available amount for sale has been exceeded.
          <br />
          <br />
          <strong>
            Please refresh and submit within the available balance.
          </strong>
        </>
      );
      break;
    case "ERR:NO FARM":
      msg = (
        <>
          You tried to extend a farm that no longer exists.
          <br />
          <br />
          <strong>Please reload the page and try again.</strong>
        </>
      );
      break;
    case "ERR:APP ASA":
      msg = (
        <>
          Mismatch between farm ASA and dualSTAKE ASA.
          <br />
          <br />
          <strong>
            This should never happen. Please let our team know about this error.
          </strong>
        </>
      );
      break;
    case "ERR:NO PAY":
    case "ERR:AXFER RCV":
    case "ERR:AXFER ID":
    case "ERR:AXFER AMT":
    case "ERR:PAY RCV":
    case "ERR PAY":
    case "ERR PAY AMT":
    case "ERR AID":
      msg = (
        <>
          Payment validation failed: {code}
          <br />
          <br />
          <strong>
            {" "}
            This should never happen. Please let our team know about this error.
          </strong>{" "}
          Sending diagnostics will help investigate the issue.
        </>
      );
      break;
    case "ERR OVER":
      msg = (
        <>
          Attempted withdrawal would exceed available amount.
          <br />
          <br />
          <strong>
            Please try again. If this error persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR NF":
    case "ERR FEE":
      msg = (
        <>
          Attempted to change node runner while there were fees accumulated.
          <br />
          <br />
          <strong>
            Please withdraw noderunner fees and try again. If this error
            persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR ZERO":
      msg = (
        <>
          Attempted to withdraw zero amount.
          <br />
          <br />
          <strong>
            Please try again. If this error persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR IE":
      msg = (
        <>
          Incentive Eligibility fee found, but was not required.
          <br />
          <br />
          <strong>
            Please try again. If this error persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR !IE":
      msg = (
        <>
          Incentive Eligibility fee was not found, but it was required.
          <br />
          <br />
          <strong>
            Please try again. If this error persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR:MBR":
      msg = (
        <>
          Payment validation failed: {code}
          <br />
          <br />
          <strong>
            Please try again. If this error persist, contact our team.
          </strong>
        </>
      );
      break;
    case "ERR:ZERO AMT":
      msg = (
        <>
          You tried to buy with zero amount, which is not allowed.
          <br />
          <br />
          <strong>Please try again with a non-zero amount.</strong>
        </>
      );
      break;
    case "ERR:QUOTA":
      msg = (
        <>
          Your purchase would have exceeded your account buy limit.
          <br />
          <br />
          <strong>Please reload the page and try again.</strong>
        </>
      );
      break;
    case "ERR:EXISTS":
      msg = (
        <>
          The farm you are trying to create already exists.
          <br />
          <br />
          <strong>Please reload the page and try again.</strong> Let our team
          know if this error persists.
        </>
      );
      break;
    case "ERR NO UPG":
      msg = (
        <>
          You attempted to protest stake, but there is no contract upgrade
          scheduled.
        </>
      );
      break;
    case "ERR MIN PRTST":
      msg = <>Protesting amount was under minimum (1 ALGO equivalent)</>;
      break;
    case "ERR NO PRTST":
      msg = (
        <>
          Protesting stake was not found. Check if your withdrawal has already
          been processed.
        </>
      );
      break;
    case "ERR RATE":
      msg = (
        <>
          The dualSTAKE to ASA exchange rate has changed. You can retry this
          error.
          <br />
          <br />
          <strong>
            Please refresh the page and re-submit your transaction.
          </strong>
        </>
      );
      break;
    case "ERR MIN AMT":
      msg = (
        <>
          The amount minted is less than minimum allowed.
          <br />
          <br />
          <strong>Try a minting with at least 1 ALGO.</strong>
        </>
      );
      break;
    case "ERR DURATION":
      msg = (
        <>
          Maximum duration exceeded.
          <br />
          <br />
          <strong>Please refresh the page and try again.</strong>
        </>
      );
      break;
    case "ERR MAX":
      msg = (
        <>
          This transaction would exceed the maximum stake allowed by the
          contract.
          <br />
          <br />
          <strong>Try a lower amount.</strong>
        </>
      );
      break;
    case "ERR:TXN EXPIRED":
      msg = (
        <>
          Transactions have expired.
          <br />
          <br />
          <strong>
            Try signing faster, or increasing your transaction lifetime.
          </strong>
          <br />
          <br />
          <div className="flex items-center gap-1 flex-wrap">
            You can change this under Settings <CogIcon /> at the top right of
            the page.
          </div>
        </>
      );
      break;
  }
  return msg ? msg : code;
}

export function ErrorComponent({
  error,
  diagnostics,
  retry,
}: {
  error: string | Error;
  diagnostics?: string;
  retry?: () => any;
}) {
  const errorMessage = error instanceof Error ? error.message : error;
  let parsedErrorCode = parseContractError(errorMessage);
  let algodErrorMessage: string | undefined;
  if (!parsedErrorCode) {
    const { code, message } = parseAlgodError(errorMessage) ?? {};
    if (code) parsedErrorCode = code;
    if (message) algodErrorMessage = message;
  }
  const diagnosticsValue = `code=${parsedErrorCode} err=${error} txns=${diagnostics}`;
  useEffect(() => {
    const eventId = Sentry.captureException(error, {
      extra: { parsedErrorCode, diagnosticsValue },
    });
    console.log("Sentry event ID", eventId);
  }, []);
  return (
    <>
      <div className="flex flex-col break-all max-h-[50vh] max-w-full">
        <div className="text-destructive  overflow-y-auto">
          {parsedErrorCode ? (
            <div className="break-normal text-foreground">
              <span className="text-muted-foreground text-sm">
                {parsedErrorCode} /
              </span>{" "}
              {algodErrorMessage ?? errorCodetoMessage(parsedErrorCode)}
            </div>
          ) : (
            errorMessage
          )}
        </div>
        <div className="flex flex-wrap gap-1 items-center mt-2">
          {!retry ? null : (
            <Button variant="secondary" size="sm" onClick={retry}>
              <RefreshCcw /> RETRY
            </Button>
          )}
          <CopyButton value={errorMessage} suffix="ERROR" />
          {diagnostics ? (
            <>
              <CopyButton value={diagnosticsValue} suffix="DIAGNOSTICS" />
              <HelpButton title="What are diagnostics?">
                <div>
                  Diagnostics encode your <strong>unsigned</strong> transactions
                  along with the full error, as received from the network.
                </div>
                <div>
                  It contains information like your account address, transaction
                  arguments, etc.
                </div>
                <div>
                  Share this with{" "}
                  <Link to="https://docs.myth.finance/dualSTAKE/contact">
                    our team
                  </Link>{" "}
                  to help us investigate your issue faster.
                </div>
              </HelpButton>
            </>
          ) : null}
        </div>
      </div>
      <div className="mt-4 text-muted-foreground">
        If you require assistance, copy the{" "}
        {diagnostics ? "diagnostics" : "error"} and share{" "}
        {diagnostics ? "them" : "it"} with{" "}
        <Link to="https://docs.myth.finance/dualSTAKE/contact">our team</Link>.
      </div>
    </>
  );
}
